import React from 'react';
import Mutation from 'react-apollo/MutationWithProgress';
import { MutationComponentOptions } from '@apollo/client/react/components';
import ImageView from 'lib/images/image-view';
import Upload, { Props as UploadProps } from 'four-nets/ui-v2/upload/upload';
import uploadStyles from 'four-nets/ui-v2/upload/upload.scss';
import uploadPhotoFileMutation, {
    UploadPhotofileMutation,
    UploadPhotofileMutationVariables,
    UploadedPhotoFileFragment,
} from './mutations/upload-photofile.graphql';

export type UploadedPhotoFile = UploadedPhotoFileFragment;

export interface Props {
    children?: any;
    file?: File | null;
    sizes?: object | string;
    url?: string;
    photoFile?: UploadedPhotoFileFragment | null;
    onClose?: UploadProps['onClose'];
    onPhotoFile: (photofile: UploadedPhotoFileFragment) => void;
    automaticUpload?: boolean;
    uploadStyle?: Partial<typeof uploadStyles>;
}

interface State {
    progress: number;
    photofile?: UploadedPhotoFile | null;
}

export const withUpload = <T extends {}>(Component: React.ComponentType<T>) =>
    class UploadPhotofile extends React.PureComponent<Props, State> {
        static defaultProps = {
            automaticUpload: true,
        };

        uploadFn!: MutationComponentOptions<UploadPhotofileMutation, UploadPhotofileMutationVariables>;
        state: State = {
            progress: 0,
            photofile: this.props.photoFile,
        };

        componentDidMount() {
            if (this.props.automaticUpload) {
                this.upload();
            }
        }

        onProgress = (event: ProgressEvent) => {
            this.setState({
                progress: event.loaded / event.total,
            });
        };

        onCompleted = (data: UploadPhotofileMutation) => {
            if (data.uploadPhotoFiles && data.uploadPhotoFiles.photoFiles && data.uploadPhotoFiles.photoFiles.length) {
                const photofile = data.uploadPhotoFiles.photoFiles[0]!;

                this.setState(
                    {
                        photofile,
                    },
                    () => {
                        if (this.props.onPhotoFile) {
                            this.props.onPhotoFile(photofile);
                        }
                    }
                );
            }
        };

        upload = () => {
            if (this.props.file) {
                this.uploadFn({ variables: { file: this.props.file } });
            }
        };

        componentDidUpdate(prevProps: Props) {
            if (this.props.file !== prevProps.file && this.props.automaticUpload) {
                this.upload();
            }
        }

        render() {
            return (
                <Mutation<UploadPhotofileMutation, UploadPhotofileMutationVariables>
                    mutation={uploadPhotoFileMutation}
                    onCompleted={this.onCompleted}
                    onProgress={this.onProgress}
                >
                    {(uploadFn, { error }) => {
                        this.uploadFn = uploadFn;
                        const { photofile, progress } = this.state;

                        return (
                            <Component
                                progress={progress}
                                error={error}
                                upload={this.upload}
                                uploadFn={uploadFn}
                                photofile={photofile}
                                {...this.props}
                            />
                        );
                    }}
                </Mutation>
            );
        }
    };

const DefaultPhotofileUpload = withUpload<any>(
    ({ children, sizes, onClose, progress, error, photofile, upload, url, uploadStyle }: any) => {
        const { srcset = '', ...rest } = photofile || {};

        return (
            <Upload style={uploadStyle} progress={progress} error={error} onRetry={upload} onClose={onClose}>
                {children}
                {photofile ? (
                    <ImageView {...rest} width='100%' height='100%' sizes={sizes} srcSet={srcset} aspectFill={true} />
                ) : (
                    <ImageView width='100%' height='100%' sizes={sizes} srcSet={srcset} src={url} aspectFill={true} />
                )}
            </Upload>
        );
    }
);

(DefaultPhotofileUpload as any).defaultProps = {
    sizes: { xs: 90 },
};

export default DefaultPhotofileUpload;
